import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, Mail, Trash2, XCircle } from 'lucide-react';
import apiService from '../services/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';

interface CompanyRetention {
  companyId: number;
  companyName: string;
  dataRetentionStatus: string;
  cancelledDate: string | null;
  scheduledDeletionDate: string | null;
  daysRemaining: number | null;
  warningEmailSent: boolean;
  lastDataExportedAt: string | null;
}

const DataRetentionPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  const { data, isLoading } = useQuery({
    queryKey: ['data-retention-companies'],
    queryFn: async () => {
      const response = await apiService.get('/superadmin/data-retention/companies');
      return response.data || [];
    },
  });

  const companies = (data || []) as CompanyRetention[];
  
  const stats = {
    active: companies.filter(c => c.dataRetentionStatus === 'Active').length,
    grace: companies.filter(c => c.dataRetentionStatus === 'Cancelled' || c.dataRetentionStatus === 'PendingDeletion').length,
    urgent: companies.filter(c => c.daysRemaining !== null && c.daysRemaining <= 7 && c.daysRemaining > 0).length,
    deleted: companies.filter(c => c.dataRetentionStatus === 'Deleted').length
  };

  const sendWarningsMutation = useMutation({
    mutationFn: () => apiService.post('/superadmin/data-retention/send-warnings-bulk'),
    onSuccess: () => {
      toast.success('Warning emails sent');
      queryClient.invalidateQueries({ queryKey: ['data-retention-companies'] });
    },
  });

  const getStatusColor = (status: string) => {
    if (status === 'Active') return 'bg-green-100 text-green-800';
    if (status === 'Cancelled') return 'bg-yellow-100 text-yellow-800';
    if (status === 'PendingDeletion') return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getDaysColor = (days: number | null) => {
    if (!days || days <= 0) return 'bg-red-100 text-red-800';
    if (days <= 7) return 'bg-orange-100 text-orange-800';
    if (days <= 30) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Data Retention Management</h1>
        <Button onClick={() => sendWarningsMutation.mutate()} disabled={sendWarningsMutation.isPending || stats.urgent === 0}>
          <Mail className="h-4 w-4 mr-2" />
          Send Warnings to All ({stats.urgent})
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Grace Period</p>
              <p className="text-2xl font-bold">{stats.grace}</p>
            </div>
            <Clock className="h-12 w-12 text-yellow-500" />
          </div>
        </Card>
        <Card className="p-6 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Urgent</p>
              <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
            </div>
            <AlertTriangle className="h-12 w-12 text-red-600" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Deleted</p>
              <p className="text-2xl font-bold">{stats.deleted}</p>
            </div>
            <Trash2 className="h-12 w-12 text-gray-400" />
          </div>
        </Card>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Left</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exported</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center">Loading...</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center">No companies found</td></tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.companyId}>
                    <td className="px-6 py-4">{company.companyName}</td>
                    <td className="px-6 py-4">
                      <Badge className={getStatusColor(company.dataRetentionStatus)}>{company.dataRetentionStatus}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      {company.daysRemaining !== null && (
                        <Badge className={getDaysColor(company.daysRemaining)}>
                          {company.daysRemaining <= 0 ? 'Expired' : `${company.daysRemaining} days`}
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {company.lastDataExportedAt ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default DataRetentionPage;

